import React, { FC } from 'react';
import { useIntercom } from 'react-use-intercom';
import {
  Banner,
  Body,
  Button,
  Small,
  TextArea,
  TrevorIcon,
  SearchField,
  Divider,
} from '@trevor/ui-components';
import {
  TArticle,
  useGetArticlesBySearchQuery,
} from '@/features/zendesk/apiSlice';
import { debounce } from '@/util/fp';
import {
  ColumnGap12Container,
  ColumnGap4Container,
  HowDoICardContainer,
  LoadingIcon,
  RelatedArticleContainer,
  RelatedArticleList,
  ShowMore,
} from '@/components/HowDoISearch/HowDoISearch.styles';
import { userAction } from '@/util/helpers/ajax-helpers';

const debounce2s = debounce(2000);
const numberOfTopArticles = 3;

const getTextForIntercom = (search: string, moreDetailsText: string) =>
  `Hi - can you help me out?\n\nSearch term: ${search}\nMore details: ${moreDetailsText}`;

type RelatedArticleProps = TArticle & { onClick: F0 };
const RelatedArticle: FC<RelatedArticleProps> = ({ title, url, onClick }) => {
  return (
    <RelatedArticleContainer>
      <a href={url} target="_blank" onClick={onClick}>
        <Body variant={2}>{title}</Body>
        <TrevorIcon name="chevron-right" />
      </a>
    </RelatedArticleContainer>
  );
};

export const HowDoISearch = () => {
  const [search, setSearch] = React.useState('');
  const [moreDetailsText, setMoreDetailsText] = React.useState('');
  const { showNewMessage } = useIntercom();
  const [showMore, setShowMore] = React.useState(false);
  const {
    data: relatedArticles = [],
    isFetching,
    isSuccess,
  } = useGetArticlesBySearchQuery({ search }, { skip: !search.length });

  const watchSearchChange = debounce2s((value: string) => {
    setSearch(value);
  });

  const handleOpenNewMessageIntercom = () => {
    userAction('how-do-i-more-details', { search, details: moreDetailsText });

    showNewMessage(getTextForIntercom(search, moreDetailsText));
    setMoreDetailsText('');
  };

  const handleArticleClick = (article: TArticle) => {
    const { id, title, url } = article;
    userAction('how-do-i-article-search-article-click', { id, title, url });
  };

  const handleSearch = (value: string) => {
    if (value) {
      watchSearchChange(value);
    } else {
      setSearch('');
    }
  };

  React.useEffect(() => {
    if (!isSuccess || !search) return;
    userAction('how-do-i-search', {
      search,
      articles: relatedArticles.length,
    });
    setShowMore(false);
  }, [relatedArticles]);

  const showShowMoreButton =
    relatedArticles.length > numberOfTopArticles && !showMore;

  return (
    <HowDoICardContainer>
      <ColumnGap12Container>
        <div>
          <Body variant={1}>How do I...?</Body>
          <Small>Tell us what you need help with</Small>
        </div>
        <SearchField
          onChange={handleSearch}
          onEnter={(value) => setSearch(value)}
        />
      </ColumnGap12Container>
      {search && !isFetching && (
        <>
          <ColumnGap4Container>
            <Small>Related articles</Small>
            {relatedArticles.length ? (
              <>
                <RelatedArticleList>
                  {(showMore
                    ? relatedArticles
                    : relatedArticles.slice(0, numberOfTopArticles)
                  ).map((article) => (
                    <RelatedArticle
                      key={article.id}
                      {...article}
                      onClick={() => handleArticleClick(article)}
                    />
                  ))}
                </RelatedArticleList>
                {showShowMoreButton && (
                  <ShowMore onClick={() => setShowMore(true)}>
                    Show more
                  </ShowMore>
                )}
              </>
            ) : (
              <Banner severity="warning">
                No results for "{search}". Try searching another keyword
              </Banner>
            )}
          </ColumnGap4Container>
          <Divider />

          <ColumnGap12Container>
            <Small>Can you give us more details?</Small>
            <TextArea
              value={moreDetailsText}
              onChange={(value: string) => setMoreDetailsText(value)}
              placeholder="Any additional information we can use to help you."
            />
          </ColumnGap12Container>
          <Button variant="secondary" onClick={handleOpenNewMessageIntercom}>
            Send us a message
          </Button>
        </>
      )}
      {isFetching && <LoadingIcon />}
    </HowDoICardContainer>
  );
};
